import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getAdminToken } from '../api/admin';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
};

const isPushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isInStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const PushToggle = () => {
  const [status, setStatus] = useState('idle'); // idle | subscribed | denied | unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) { setStatus('unsupported'); return; }
      if (Notification.permission === 'denied') { setStatus('denied'); return; }
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        const sub = await reg.pushManager.getSubscription();
        setStatus(sub ? 'subscribed' : 'idle');
      } catch (err) {
        console.warn('SW register failed:', err);
      }
    })();
  }, []);

  const subscribe = async () => {
    if (!isPushSupported()) {
      toast({ title: 'Notifications not supported', description: 'Your browser does not support push notifications.' });
      return;
    }
    if (isIOS() && !isInStandalone()) {
      toast({
        title: 'Install app first',
        description: 'On iPhone/iPad, first install this portal via Safari → Share → Add to Home Screen. Then open the installed app and enable alerts.'
      });
      return;
    }
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setStatus('denied'); setBusy(false); return; }
      const reg = await navigator.serviceWorker.register('/sw.js');
      // Fetch VAPID public key
      const pkRes = await axios.get(`${API}/admin/push/public-key`, { headers: { 'X-Admin-Token': getAdminToken() } });
      const applicationServerKey = urlBase64ToUint8Array(pkRes.data.public_key);
      // Subscribe
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      const raw = sub.toJSON();
      await axios.post(`${API}/admin/push/subscribe`, {
        endpoint: raw.endpoint,
        keys: raw.keys
      }, { headers: { 'X-Admin-Token': getAdminToken() } });
      setStatus('subscribed');
      toast({ title: 'Alerts on', description: "You'll get a ping for every new order." });
    } catch (err) {
      console.error(err);
      toast({ title: 'Could not enable', description: err.message });
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.delete(`${API}/admin/push/subscribe`, {
          headers: { 'X-Admin-Token': getAdminToken() },
          params: { endpoint: sub.endpoint }
        });
        await sub.unsubscribe();
      }
      setStatus('idle');
      toast({ title: 'Alerts off' });
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    try {
      const res = await axios.post(`${API}/admin/push/test`, {}, { headers: { 'X-Admin-Token': getAdminToken() } });
      toast({ title: 'Test sent', description: `${res.data.sent} device(s) pinged` });
    } catch (err) {
      toast({ title: 'Test failed', description: err.message });
    }
  };

  const iosNotStandalone = isIOS() && !isInStandalone();

  return (
    <div className="border rounded p-4 bg-white flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center ${status === 'subscribed' ? 'bg-[#22c55e] text-black' : 'bg-gray-100 text-gray-500'}`}>
          {status === 'subscribed' ? <BellRing size={22} /> : status === 'denied' ? <BellOff size={22} /> : <Bell size={22} />}
        </div>
        <div>
          <div className="font-bold text-sm">Order Alerts</div>
          <p className="text-xs text-gray-600">
            {status === 'subscribed' && 'Enabled on this device – you\'ll be pinged for every new order.'}
            {status === 'idle' && 'Get a notification the moment a customer places an order.'}
            {status === 'denied' && 'Blocked. Enable notifications for this site in your device settings.'}
            {status === 'unsupported' && 'Your browser does not support push notifications.'}
          </p>
          {iosNotStandalone && (
            <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
              <AlertCircle size={12} /> On iPhone: install the portal to your Home Screen first, then open it and turn alerts on there.
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        {status === 'subscribed' ? (
          <>
            <Button variant="outline" onClick={sendTest} disabled={busy}>Send test</Button>
            <Button onClick={unsubscribe} disabled={busy} className="bg-gray-800 hover:bg-black text-white font-bold">TURN OFF</Button>
          </>
        ) : (
          <Button onClick={subscribe} disabled={busy || status === 'unsupported'} className="bg-[#22c55e] hover:bg-[#16a34a] font-bold tracking-wider">
            {busy ? 'ENABLING...' : 'ENABLE ALERTS'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PushToggle;
